import { inject, Injectable } from '@angular/core';
import { EjournalApiService } from '@et/api';
import { NotificationService } from '@et/notifications';
import {
  AddEtJournalEntryDto,
  EtJournalEntry,
  EtJournalEntryWithId,
  EtJournalESeal,
  EtJournalSigner,
  EtJournalSignerWithId,
  PatchEtJournalSignerDto,
} from '@et/typings';
import { getCurrentUTCDate } from '@et/utils';
import { Store } from '@ngxs/store';
import { catchError, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import {
  UpdateEntries,
  UpdateSigners,
} from '../state/ejournal-session.actions';
import { EjournalSessionSelectors } from '../state/ejournal-session.selectors';
import { EjournalSessionCancelService } from './ejournal-session-cancel.service';

@Injectable()
export class EjournalEntriesService {
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly sessionCancel = inject(EjournalSessionCancelService);
  private readonly store = inject(Store);

  private readonly eJournal = this.store.selectSignal(
    EjournalSessionSelectors.getEjournal,
  );
  private readonly signers = this.store.selectSignal(
    EjournalSessionSelectors.getSigners,
  );
  private readonly ip = this.store.selectSignal(EjournalSessionSelectors.getIp);

  /**
   * Sorts entries by applying, in order of precedence:
   * 1. Cancellation actions are always pushed to the bottom.
   * 2. Page number ascending (entries without a page number come after
   *    entries that have one).
   * 3. Notarized date ascending (entries without a date sort last).
   */
  static sortEntries(entries: EtJournalEntry[]) {
    return [...entries].sort((a, b) => {
      const aCancelled = !!a.cancelActUtcDate;
      const bCancelled = !!b.cancelActUtcDate;
      if (aCancelled !== bCancelled) return aCancelled ? 1 : -1;

      const aHasPage = a.pageNumber != null;
      const bHasPage = b.pageNumber != null;
      if (aHasPage !== bHasPage) return aHasPage ? -1 : 1;
      if (aHasPage && bHasPage && a.pageNumber !== b.pageNumber) {
        return (a.pageNumber as number) - (b.pageNumber as number);
      }

      const aTime = a.notarizationDate
        ? new Date(a.notarizationDate).getTime()
        : Infinity;
      const bTime = b.notarizationDate
        ? new Date(b.notarizationDate).getTime()
        : Infinity;
      return aTime - bTime;
    });
  }

  /**
   * Deletes an entry on the server, removes it from local `entries`, and removes its id from any linked signer's `ejournalEntryIds`.
   */
  deleteEntry(entry: EtJournalEntry) {
    const entryId = entry.eJournalEntryId;
    return this.unlinkEntriesFromSigners([entryId]).pipe(
      this.sessionCancel.takeUntilCancel(),
      switchMap(() => this.ejournalApiService.deleteEntry(entryId)),
      tap(() => {
        this.notificationService.showSuccess('Entry deleted successfully');
        this.removeEntriesFromLocalState([entryId]);
      }),
      catchError((error) => {
        this.notificationService.showError('Failed to delete entry');
        throw error;
      }),
    );
  }

  /**
   * Creates the no-signer holder entry for a seal/eSeal.
   */
  createHolderEntry(seal: EtJournalESeal): Observable<unknown> {
    const ejournal = this.eJournal();
    if (!ejournal) {
      return of(null);
    }

    const payload: AddEtJournalEntryDto = {
      journalId: ejournal.id,
      packetId: seal.packetId || ejournal.packetId,
      pageNumber: seal.pageNumber,
      notorialAct: seal.notorialAct,
      utcCreatedOn: getCurrentUTCDate(),
      documentDescription: seal.documentDescription,
      notes: seal.notes?.trim() || undefined,
      notarizationDate: seal.notarizationDate,
      feeCharged: seal.feeCharged,
      ipAddress: this.ip() || seal.ipAddress || '',
      entryType: seal.source === 'seal' ? 'seal' : 'eSeal',
    };

    return this.ejournalApiService.addEjournalEntries([payload]).pipe(
      tap((created) => {
        const mapped = created.map((c) => this.withIdToEntry(c));
        this.updateEntries((entries) =>
          EjournalEntriesService.sortEntries([...entries, ...mapped]),
        );
      }),
    );
  }

  /**
   * Creates new entries for newly assigned seal signers and links each to its signer.
   */
  createSealEntries(
    seal: EtJournalESeal,
    creates: Array<{ signerId: string }>,
  ): Observable<unknown> {
    if (creates.length === 0) {
      return of(null);
    }

    const ejournal = this.eJournal();
    if (!ejournal) {
      return of(null);
    }

    const payloads: AddEtJournalEntryDto[] = creates.map(() => ({
      journalId: ejournal.id,
      packetId: seal.packetId || ejournal.packetId,
      pageNumber: seal.pageNumber,
      notorialAct: seal.notorialAct,
      utcCreatedOn: getCurrentUTCDate(),
      documentDescription: seal.documentDescription,
      notes: seal.notes?.trim() || undefined,
      notarizationDate: seal.notarizationDate,
      feeCharged: seal.feeCharged,
      ipAddress: this.ip() || seal.ipAddress || '',
      entryType: seal.source === 'seal' ? 'seal' : 'eSeal',
    }));

    return this.ejournalApiService.addEjournalEntries(payloads).pipe(
      switchMap((created) => {
        const mapped = created.map((c) => this.withIdToEntry(c));
        this.updateEntries((entries) =>
          EjournalEntriesService.sortEntries([...entries, ...mapped]),
        );

        const linkOps = creates.map((createOp, index) => {
          const entry = mapped[index];
          if (!entry) {
            return of(null);
          }
          return this.linkEntryToSigner(
            createOp.signerId,
            entry.eJournalEntryId,
          );
        });

        return linkOps.length > 0 ? forkJoin(linkOps) : of([]);
      }),
    );
  }

  /**
   * Appends an entry id to a signer's ejournalEntryIds via the API and updates local state.
   */
  linkEntryToSigner(signerId: string, entryId: string): Observable<unknown> {
    const signer = this.signers().find((s) => s.eJournalSignerId === signerId);
    if (!signer) {
      return of(null);
    }

    let ids: string[] = [];
    try {
      ids = JSON.parse(signer.ejournalEntryIds || '[]');
      if (!Array.isArray(ids)) {
        ids = [];
      }
    } catch {
      ids = [];
    }

    if (ids.includes(entryId)) {
      return of(null);
    }

    const patch: Partial<PatchEtJournalSignerDto> = {
      id: signerId,
      ejournalEntryIds: [...ids, entryId],
    };

    return this.ejournalApiService
      .patchSigner(signerId, patch)
      .pipe(tap((updated) => this.mergeSignerInUi(updated)));
  }

  /**
   * Removes entry ids from all linked signers' ejournalEntryIds via the API and updates local state.
   */
  unlinkEntriesFromSigners(entryIds: string[]): Observable<unknown> {
    if (entryIds.length === 0) {
      return of(null);
    }

    const idSet = new Set(entryIds);
    const patches = this.signers()
      .map((signer) => {
        let ids: string[] = [];
        try {
          ids = JSON.parse(signer.ejournalEntryIds || '[]');
          if (!Array.isArray(ids)) {
            ids = [];
          }
        } catch {
          ids = [];
        }
        const filtered = ids.filter((id) => !idSet.has(id));
        if (filtered.length === ids.length) {
          return null;
        }
        const patch: Partial<PatchEtJournalSignerDto> = {
          id: signer.eJournalSignerId,
          ejournalEntryIds: filtered,
        };
        return this.ejournalApiService
          .patchSigner(signer.eJournalSignerId, patch)
          .pipe(tap((updated) => this.mergeSignerInUi(updated)));
      })
      .filter((op): op is NonNullable<typeof op> => op !== null);

    return patches.length > 0 ? forkJoin(patches) : of(null);
  }

  /**
   * Removes entries from local state and strips their ids from all signers' ejournalEntryIds.
   */
  removeEntriesFromLocalState(entryIds: string[]) {
    const idSet = new Set(entryIds);
    this.updateEntries((entries) =>
      entries.filter((e) => !idSet.has(e.eJournalEntryId)),
    );
    this.updateSigners((signers) =>
      signers.map((s) => {
        let ids: string[] = [];
        try {
          ids = JSON.parse(s.ejournalEntryIds || '[]');
          if (!Array.isArray(ids)) {
            ids = [];
          }
        } catch {
          ids = [];
        }
        const filtered = ids.filter((id) => !idSet.has(id));
        if (filtered.length === ids.length) {
          return s;
        }
        return {
          ...s,
          ejournalEntryIds: JSON.stringify(filtered),
        };
      }),
    );
  }

  withIdToEntry(w: EtJournalEntryWithId): EtJournalEntry {
    const { id, ...rest } = w;
    return { ...rest, eJournalEntryId: id };
  }

  entryToWithId(entry: EtJournalEntry): EtJournalEntryWithId {
    const { eJournalEntryId, ...rest } = entry;
    return { ...rest, id: eJournalEntryId };
  }

  private mergeSignerInUi(updatedSigner: EtJournalSignerWithId) {
    const { id, ...signerWithoutId } = updatedSigner;
    const signer: EtJournalSigner = {
      ...signerWithoutId,
      eJournalSignerId: id,
      signatures:
        this.signers().find((s) => s.eJournalSignerId === id)?.signatures ?? [],
    };
    this.updateSigners((signers) => {
      const copy = [...signers];
      const index = copy.findIndex((s) => s.eJournalSignerId === id);
      if (index >= 0) {
        copy[index] = signer;
      }
      return copy;
    });
  }

  private updateEntries(
    updater: (entries: EtJournalEntry[]) => EtJournalEntry[],
  ) {
    this.store.dispatch(new UpdateEntries(updater));
  }

  private updateSigners(
    updater: (signers: EtJournalSigner[]) => EtJournalSigner[],
  ) {
    this.store.dispatch(new UpdateSigners(updater));
  }
}
